import {
  ArrowBack,
  ArrowForward,
  CalendarMonth,
  DateRange,
  Edit as EditIcon,
  ExpandMore,
  MyLocation,
  Person,
  Route,
  Search,
  Today,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import Moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getLinkedStationsByCity,
  getStationsByCity,
} from "../../actions/stations";
import {
  alarmBike,
  clearBike,
  clearHasBikeHistory,
  getBikeHistory,
  getBikeId,
  getBikeStatus,
  getPaginatedBikeHistory,
  lockBike,
  unlockBike,
  updateBike,
} from "../../actions/vehicle";
import BikeModal from "../../components/modal/bikeModal";
import { MAP_KEY } from "../../configs/settings";
import { BIKE_HISTORY_DAY, DATE_FORMAT_DEFAULT } from "../../constants/const";
import * as routes from "../../constants/routes";
import { useShowToast } from "../../hooks";
import greenBike from "../../images/bikes/greenBike.png";

const PAGE_SIZE = 100;
const MAX_DAYS = 15;

const BikeHistoryContainer = (props) => {
  const {
    clearBike,
    unlockBike,
    alarmBike,
    lockBike,
    stationsByCity,
    getStationsByCity,
    getLinkedStationsByCity,
    linkedStations,
    bikeTypes,
    savedCity,
    bikeHistory,
    getBikeHistory,
    adminDetails,
  } = props;

  const showToast = useShowToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { bikeId, vehicleCode, bikeName } = location.state || {};

  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [startDate, setStartDate] = useState(
    Moment().subtract(BIKE_HISTORY_DAY, "days").startOf("day")
  );
  const [endDate, setEndDate] = useState(Moment());
  const [editClicked, setEditClicked] = useState(false);
  const [bikeModalVisible, setBikeModalVisible] = useState(false);
  const [currentZoom, setZoom] = useState(14);
  const [mapInstance, setMapInstance] = useState(null);

  const hideModal = () => setBikeModalVisible(false);
  const showModal = () => setBikeModalVisible(true);

  useEffect(() => {
    if (!vehicleCode) {
      navigate("/dashboard/bikes");
    }
  }, [vehicleCode, navigate]);

  const closeModal = () => {
    clearBike();
    hideModal();
  };

  const convertDateToUtc0 = (date) => {
    const dateUtc = Moment(date).utcOffset(0);
    return dateUtc.format(DATE_FORMAT_DEFAULT);
  };

  const historyData = useMemo(() => {
    return bikeHistory?.content || [];
  }, [bikeHistory]);

  const groupedJourneys = useMemo(() => {
    const groups = {};
    historyData.forEach((item) => {
      const key = item.journeyId || "unknown";
      if (!groups[key]) {
        groups[key] = {
          journeyId: item.journeyId,
          userId: item.userId,
          username: item.username,
          journeyStatus: item.journeyStatus,
          points: [],
        };
      }
      groups[key].points.push(item);
    });
    return Object.values(groups).sort(
      (a, b) => (b.journeyId || 0) - (a.journeyId || 0)
    );
  }, [historyData]);

  const [expandedJourney, setExpandedJourney] = useState(null);

  const totalPages = useMemo(() => {
    return bikeHistory?.totalPages || 0;
  }, [bikeHistory]);

  const totalElements = useMemo(() => {
    return bikeHistory?.totalElements || 0;
  }, [bikeHistory]);

  useEffect(() => {
    if (props.updateBikeSuccess) {
      showToast("Success", "success");
      clearBike();
      hideModal();
    }
  }, [props.updateBikeSuccess, clearBike, showToast]);

  const getDateRangeInfo = () => {
    const start = Moment(startDate);
    const end = Moment(endDate);
    const diffMs = end.diff(start);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    const diffHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);

    return {
      isValid: diffDays <= MAX_DAYS && diffDays >= 0,
      isNegative: diffDays < 0,
      isTooLong: diffDays > MAX_DAYS,
      days: Math.floor(diffDays),
      hours: Math.abs(diffHours),
      totalHours: Math.floor(diffMs / (1000 * 60 * 60)),
    };
  };

  const dateRangeInfo = getDateRangeInfo();

  const setQuickRange = (days) => {
    const end = Moment();
    const start = Moment().subtract(days, "days").startOf("day");
    setStartDate(start);
    setEndDate(end);

    setZoom(14);
    setPageNumber(0);
    setSelectedPoint(null);
    setLoading(true);
    getBikeHistory(
      0,
      PAGE_SIZE,
      vehicleCode,
      convertDateToUtc0(start),
      convertDateToUtc0(end)
    );
  };

  const loadHistory = (page = 0) => {
    if (!vehicleCode) return;
    setLoading(true);
    getBikeHistory(
      page,
      PAGE_SIZE,
      vehicleCode,
      convertDateToUtc0(startDate),
      convertDateToUtc0(endDate)
    );
  };

  const handleFilter = () => {
    if (!dateRangeInfo.isValid) {
      if (dateRangeInfo.isNegative) {
        showToast("End date must be after start date", "error");
      } else {
        showToast(`Date range cannot exceed ${MAX_DAYS} days`, "error");
      }
      return;
    }
    setZoom(14);
    setPageNumber(0);
    setSelectedPoint(null);
    loadHistory(0);
  };

  useEffect(() => {
    if (vehicleCode) {
      loadHistory(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleCode]);

  useEffect(() => {
    if (bikeHistory !== null) {
      setLoading(false);
    }
  }, [bikeHistory]);

  useEffect(() => {
    if (editClicked && props.getBikeByIdSuccess) showModal();
  }, [editClicked, props.getBikeByIdSuccess, savedCity]);

  const showBikeInfo = () => {
    props.getBikeId(bikeId);
    props.getBikeStatus();
    setEditClicked(true);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
    loadHistory(newPage);
  };

  const setPointOnMap = (item) => {
    setSelectedPoint(item);
    if (mapInstance && item) {
      mapInstance.panTo({
        lat: parseFloat(item.latitude),
        lng: parseFloat(item.longitude),
      });
    }
  };

  const remapToPath = (data) =>
    data
      ?.map((item) => ({
        lat: parseFloat(item.latitude),
        lng: parseFloat(item.longitude),
      }))
      .filter((p) => !isNaN(p.lat) && !isNaN(p.lng)) || [];

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAP_KEY,
  });

  const mapCenter = useMemo(() => {
    if (historyData.length > 0) {
      const first = historyData[0];
      return {
        lat: parseFloat(first.latitude),
        lng: parseFloat(first.longitude),
      };
    }
    return { lat: 59.91, lng: 10.75 };
  }, [historyData]);

  const pathOptions = {
    strokeColor: "#2196f3",
    strokeOpacity: 0.8,
    strokeWeight: 3,
  };

  if (!vehicleCode) return null;

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton onClick={() => navigate(-1)} size="small">
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" component="h1">
                Vehicle History
              </Typography>
              <Chip label={bikeName || vehicleCode} color="primary" />
              {totalElements > 0 && (
                <Chip
                  label={`${totalElements} ${totalElements === 1 ? "location" : "locations"} found`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={showBikeInfo}
            >
              Edit Vehicle
            </Button>
          </Box>

          <Box sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Quick select:
              </Typography>
              <ButtonGroup size="small" variant="outlined">
                <Button onClick={() => setQuickRange(1)} startIcon={<Today />}>
                  Today
                </Button>
                <Button onClick={() => setQuickRange(3)}>3 days</Button>
                <Button
                  onClick={() => setQuickRange(7)}
                  startIcon={<DateRange />}
                >
                  7 days
                </Button>
                <Button
                  onClick={() => setQuickRange(14)}
                  startIcon={<CalendarMonth />}
                >
                  14 days
                </Button>
              </ButtonGroup>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <DateTimePicker
                label="Start Date"
                value={startDate}
                onChange={(value) => setStartDate(Moment(value))}
                inputVariant="outlined"
                variant="inline"
                format="DD/MM/YYYY HH:mm"
                ampm={false}
                maxDate={Moment()}
              />
              <DateTimePicker
                label="End Date"
                value={endDate}
                onChange={(value) => setEndDate(Moment(value))}
                inputVariant="outlined"
                variant="inline"
                format="DD/MM/YYYY HH:mm"
                ampm={false}
                maxDate={Moment()}
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Search />
                    )
                  }
                  onClick={handleFilter}
                  disabled={isLoading || !dateRangeInfo.isValid}
                  sx={{ height: 56 }}
                >
                  {isLoading ? "Loading..." : "Search"}
                </Button>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: 56,
                }}
              >
                {dateRangeInfo.isValid ? (
                  <Chip
                    label={`${dateRangeInfo.days}d ${dateRangeInfo.hours}h selected`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ) : dateRangeInfo.isNegative ? (
                  <Chip
                    label="End date must be after start"
                    color="error"
                    size="small"
                  />
                ) : (
                  <Chip
                    label={`Exceeds ${MAX_DAYS} day limit`}
                    color="error"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        <Card sx={{ flex: 2, minHeight: 500 }}>
          <CardContent sx={{ height: "100%", p: 0 }}>
            {loadError ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 500,
                }}
              >
                <Typography color="error">Failed to load map</Typography>
              </Box>
            ) : !isLoaded ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 500,
                }}
              >
                <CircularProgress />
              </Box>
            ) : isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 500,
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <CircularProgress />
                <Typography color="text.secondary">
                  Loading location data...
                </Typography>
              </Box>
            ) : historyData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 500,
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <MyLocation sx={{ fontSize: 64, color: "#ccc" }} />
                <Typography color="text.secondary">
                  No location history found for this date range
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Try selecting a different date range or check if the vehicle
                  has had rentals
                </Typography>
              </Box>
            ) : (
              <GoogleMap
                mapContainerStyle={{ height: 500, width: "100%" }}
                zoom={currentZoom}
                center={mapCenter}
                onLoad={setMapInstance}
                onUnmount={() => setMapInstance(null)}
              >
                <Polyline
                  path={remapToPath(historyData)}
                  options={pathOptions}
                />
                {historyData.length > 0 && (
                  <>
                    <Marker
                      icon={{
                        url: greenBike,
                        scaledSize: mapInstance
                          ? new window.google.maps.Size(30, 30)
                          : null,
                      }}
                      position={remapToPath(historyData)[0]}
                      title="Most Recent"
                    />
                    <Marker
                      icon="https://developers.google.com/maps/documentation/javascript/examples/full/images/beachflag.png"
                      position={remapToPath(historyData).slice(-1)[0]}
                      title="Oldest"
                    />
                  </>
                )}
                {selectedPoint && (
                  <Marker
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      fillColor: "#ffc107",
                      fillOpacity: 1,
                      scale: 10,
                      strokeColor: "#ff9800",
                      strokeWeight: 2,
                    }}
                    position={{
                      lat: parseFloat(selectedPoint.latitude),
                      lng: parseFloat(selectedPoint.longitude),
                    }}
                    title="Selected Point"
                  />
                )}
              </GoogleMap>
            )}
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 400 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h6">Journeys</Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  size="small"
                  onClick={() => handlePageChange(Math.max(0, pageNumber - 1))}
                  disabled={pageNumber === 0 || isLoading}
                >
                  <ArrowBack />
                </IconButton>
                <Chip
                  label={`${pageNumber + 1} / ${Math.max(1, totalPages)}`}
                  size="small"
                  sx={{ mx: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={() => handlePageChange(pageNumber + 1)}
                  disabled={pageNumber >= totalPages - 1 || isLoading}
                >
                  <ArrowForward />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ maxHeight: 420, overflow: "auto" }}>
              {groupedJourneys.length === 0 ? (
                <Typography
                  color="text.secondary"
                  sx={{ py: 4, textAlign: "center" }}
                >
                  {isLoading ? "Loading..." : "No data available"}
                </Typography>
              ) : (
                groupedJourneys.map((journey) => (
                  <Accordion
                    key={journey.journeyId}
                    expanded={expandedJourney === journey.journeyId}
                    onChange={(_, isExpanded) =>
                      setExpandedJourney(isExpanded ? journey.journeyId : null)
                    }
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      "&:before": { display: "none" },
                      boxShadow: 1,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{ px: 2, py: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          width: "100%",
                          flexWrap: "wrap",
                        }}
                      >
                        <Link
                          href={`${routes.DASHBOARD_RENTALS}?rentId=${journey.journeyId}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(
                              `${routes.DASHBOARD_RENTALS}?rentId=${journey.journeyId}`
                            );
                          }}
                          underline="hover"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                          }}
                        >
                          <Route fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="600">
                            Rental #{journey.journeyId}
                          </Typography>
                        </Link>
                        {journey.userId && (
                          <Link
                            href={
                              adminDetails?.newBackendEnabled && savedCity?.id
                                ? `/dashboard/customer/${journey.userId}/${savedCity.id}`
                                : savedCity?.id
                                  ? `/dashboard/user/${journey.userId}/${savedCity.id}`
                                  : `/dashboard/user/${journey.userId}`
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                adminDetails?.newBackendEnabled &&
                                savedCity?.id
                              ) {
                                navigate(
                                  `/dashboard/customer/${journey.userId}/${savedCity.id}`
                                );
                              } else if (savedCity?.id) {
                                navigate(
                                  `/dashboard/user/${journey.userId}/${savedCity.id}`
                                );
                              } else {
                                navigate(`/dashboard/user/${journey.userId}`);
                              }
                            }}
                            underline="hover"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Person fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {journey.username || `User ${journey.userId}`}
                            </Typography>
                          </Link>
                        )}
                        <Box
                          sx={{
                            ml: "auto",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={journey.journeyStatus || "-"}
                            size="small"
                            color={
                              journey.journeyStatus === "ACTIVE"
                                ? "success"
                                : "default"
                            }
                          />
                          <Chip
                            label={`${journey.points.length} ${journey.points.length === 1 ? "location" : "locations"}`}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, py: 0, pb: 2 }}>
                      {journey.points.length > 10 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Showing {journey.points.length} locations (scroll to
                          see more)
                        </Typography>
                      )}
                      <TableContainer
                        sx={{
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          maxHeight: 300,
                        }}
                      >
                        <Table size="small" stickyHeader={true}>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  backgroundColor: "grey.100",
                                }}
                              >
                                Time
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  backgroundColor: "grey.100",
                                }}
                              >
                                GPS Coordinates
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {journey.points.map((item, index) => (
                              <TableRow
                                key={`${item.journeyId}-${index}`}
                                hover={true}
                                onClick={() => setPointOnMap(item)}
                                sx={{
                                  cursor: "pointer",
                                  backgroundColor:
                                    selectedPoint?.dateUpdated ===
                                    item.dateUpdated
                                      ? "primary.light"
                                      : "inherit",
                                  "&:hover": {
                                    backgroundColor:
                                      selectedPoint?.dateUpdated ===
                                      item.dateUpdated
                                        ? "primary.light"
                                        : "action.hover",
                                  },
                                }}
                              >
                                <TableCell
                                  sx={{ whiteSpace: "nowrap", py: 1.5 }}
                                >
                                  {Moment(item.dateUpdated)
                                    .local()
                                    .format("DD/MM HH:mm:ss")}
                                </TableCell>
                                <TableCell sx={{ py: 1.5 }}>
                                  <Tooltip
                                    title="Click to view on map"
                                    arrow={true}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: "monospace",
                                        color: "text.secondary",
                                      }}
                                    >
                                      {parseFloat(item.latitude).toFixed(5)},{" "}
                                      {parseFloat(item.longitude).toFixed(5)}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <BikeModal
        visible={bikeModalVisible}
        clearBike={closeModal}
        bike={props.bike}
        statuses={props.statuses}
        updateBike={props.updateBike}
        cityNameList={props.citiesList}
        unlockBike={unlockBike}
        alarmBike={alarmBike}
        lockBike={lockBike}
        stations={stationsByCity}
        getStations={getStationsByCity}
        getLinkedStations={getLinkedStationsByCity}
        linkedStations={linkedStations}
        bikeTypes={bikeTypes}
      />
    </Box>
  );
};

const mapStateToProps = ({
  vehicle: {
    bike,
    updateBikeSuccess,
    getBikeByIdSuccess,
    statuses,
    bikeTypes,
    bikeHistory,
  },
  cities: { citiesList, savedCity },
  stations: { stationsByCity, linkedStations },
  auth: { adminDetails },
}) => ({
  bike,
  updateBikeSuccess,
  getBikeByIdSuccess,
  citiesList,
  stationsByCity,
  linkedStations,
  statuses,
  bikeTypes,
  savedCity,
  bikeHistory,
  adminDetails,
});

const mapDispatchToProps = {
  updateBike,
  unlockBike,
  alarmBike,
  lockBike,
  getBikeId,
  getLinkedStationsByCity,
  getStationsByCity,
  getBikeStatus,
  getBikeHistory,
  clearBike,
  clearHasBikeHistory,
  getPaginatedBikeHistory,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BikeHistoryContainer);
